import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Crown, BookOpen, Heart } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { optimizedImageUrl } from '../utils/optimizedImage';

const coverColors = [
  'from-night-blue/80 to-night-blue/60',
  'from-old-rose/80 to-old-rose/60',
  'from-gold/60 to-gold/40',
  'from-deep-blue/80 to-night-blue/60',
  'from-old-rose/60 to-gold/40',
];

const BookCover = ({ story, index, locked = false, isFavorite = false, onToggleFavorite, progress }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loaded, setLoaded] = useState(false);
  const colorClass = coverColors[index % coverColors.length];
  const hasProgress = progress && progress.current_page > 0 && progress.total_pages > 0;
  const progressPercent = hasProgress ? Math.round((progress.current_page / progress.total_pages) * 100) : 0;
  const thumbUrl = optimizedImageUrl(story.cover_image_url, 'thumb');

  return (
    <motion.div
      data-testid={`book-cover-${story.id}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index, 10) * 0.04, duration: 0.4 }}
      className="book-cover-hover cursor-pointer"
      onClick={() => navigate(`/story/${story.id}`)}
    >
      <div className="relative aspect-[3/4] rounded-r-xl rounded-l-sm overflow-hidden shadow-xl book-spine bg-muted/30">
        {story.cover_image_url ? (
          <>
            {/* Skeleton tot het beeld geladen is */}
            {!loaded && (
              <div className={`absolute inset-0 bg-gradient-to-br ${colorClass} animate-pulse`} />
            )}
            <picture>
              <source srcSet={thumbUrl} type="image/webp" />
              <img
                src={story.cover_image_url}
                alt={story.title}
                loading="lazy"
                decoding="async"
                onLoad={() => setLoaded(true)}
                onError={() => setLoaded(true)}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
              />
            </picture>
          </>
        ) : (
          <div className={`absolute inset-0 bg-gradient-to-br ${colorClass} flex items-center justify-center`}>
            <BookOpen className="w-12 h-12 text-white/40" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-night-blue/80 via-night-blue/10 to-transparent pointer-events-none" />
        {locked && (
          <div className="absolute inset-0 bg-night-blue/40 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-night-blue/80 flex items-center justify-center">
              <Lock className="w-6 h-6 text-gold" />
            </div>
          </div>
        )}
        <div className="absolute left-0 top-0 bottom-0 w-3 bg-gradient-to-r from-white/20 to-transparent pointer-events-none" />
        {onToggleFavorite && (
          <button
            data-testid={`favorite-${story.id}`}
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(story.id); }}
            className={`absolute top-2 left-2 z-10 w-9 h-9 rounded-full flex items-center justify-center transition-all ${isFavorite ? 'bg-red-500/80 scale-110' : 'bg-black/30 backdrop-blur-sm hover:bg-black/50'}`}
          >
            <Heart className={`w-5 h-5 ${isFavorite ? 'text-white fill-white' : 'text-white/90'}`} />
          </button>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          {hasProgress && (
            <div className="mb-2">
              <div className="h-1 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-gold rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
              </div>
              <p className="text-cream/60 text-xs mt-0.5">
                {progressPercent === 100 ? t('misc.read') : `${progressPercent}% ${t('misc.read')}`}
              </p>
            </div>
          )}
          <h3 className="font-display text-2xl text-cream drop-shadow-lg leading-tight">{story.title}</h3>
          <p className="text-cream/80 text-sm mt-1 font-accent">{t('misc.by')} {story.grandma_name}</p>
        </div>
        <div className="absolute top-2 right-2">
          {story.is_free ? (
            <div className="px-2.5 py-1 rounded-full bg-green-500/90 text-white text-xs font-bold shadow-sm">{t('misc.free')}</div>
          ) : (
            <div className="w-8 h-8 rounded-full bg-gold/90 flex items-center justify-center shadow-sm">
              <Crown className="w-4 h-4 text-night-blue" />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default BookCover;
